import type { FocusEvent, MouseEvent, SyntheticEvent } from "react"
import {
  identify as amplitudeIdentify,
  track as amplitudeTrack,
  initAmplitude,
} from "./integrations/amplitude"
import {
  type HoneypotStatus,
  honeypotStatus,
  initHoneypot,
} from "./integrations/honeypot"
import {
  extractProperties,
  getReferralTrackingParameters,
  getWalletTrackingParameters,
} from "./lib/helpers"
import { type SamplingOptions, shouldSampleWithOptions } from "./lib/sampling"
import type { EventName, EventProperties } from "./types"

export type TrackableInterfaceEvent =
  | Event
  | MouseEvent
  | FocusEvent
  | SyntheticEvent

export async function initAnalytics({ build }: { build: string }) {
  // Amplitude handles event tracking / product analytics
  await initAmplitude({ appVersion: build })
  // Honeypot handles geofencing
  await initHoneypot()
}

export async function getHoneypotStatus(): Promise<HoneypotStatus> {
  return await honeypotStatus()
}

export async function identify(
  userId: string | undefined,
  properties?: EventProperties,
) {
  await Promise.all([amplitudeIdentify(userId, properties)])
}

export async function track(
  name: EventName,
  properties?: EventProperties,
  event?: TrackableInterfaceEvent,
  samplingOptions?: SamplingOptions,
) {
  // Apply sampling logic - skip tracking if event should not be sampled
  if (!shouldSampleWithOptions(name, samplingOptions)) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[ANALYTICS] Skipped due to sampling:", name, properties)
    }
    return
  }

  const trackingParameters = getWalletTrackingParameters()
  const referralParameters = getReferralTrackingParameters()
  await Promise.all([
    amplitudeTrack(name, {
      ...extractProperties(event),
      ...properties,
      ...trackingParameters,
      ...referralParameters,
    }),
  ])
}
